import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  AfterViewInit,
  input,
  inject,
  PLATFORM_ID,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { loadReactApp, ReactAppAPI } from './react-app-loader';
import { ConfigService } from '../../../core/services/config.service';
import { Auth0Service } from '../../../core/auth/services/auth0.service';
import { ReactAppBridgeService } from '../../services/react-app-bridge.service';

@Component({
  selector: 'app-react-app-wrapper',
  templateUrl: './react-app-wrapper.component.html',
  styleUrl: './react-app-wrapper.component.css',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactAppWrapperComponent implements AfterViewInit, OnDestroy {
  @ViewChild('reactContainer', { static: false })
  reactContainer!: ElementRef<HTMLDivElement>;

  private platformId = inject(PLATFORM_ID);
  private configService = inject(ConfigService);
  private auth0Service = inject(Auth0Service);
  private reactAppBridge = inject(ReactAppBridgeService);
  private router = inject(Router);
  basePath = input<string>('/');
  isUseAngular = input<boolean>(false);

  isLoaded = signal(false);
  hasError = signal(false);
  private reactRoot: ReactAppAPI | null = null;
  private routerSubscription: any = null;
  private lastNavigatedPath: string | null = null;

  async ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      await this.mountReactApp();
    }
  }

  private async mountReactApp() {
    try {
      const container = this.reactContainer.nativeElement;
      const basePath = this.basePath();

      if (container.hasChildNodes() && container.querySelector('[data-reactroot], [data-react-root]')) {
        console.log('React app already mounted in container, skipping remount');
        this.isLoaded.set(true);

        return;
      }

      if (this.reactRoot) {
        try {
          if (typeof this.reactRoot.unmount === 'function') {
            this.reactRoot.unmount();
          }
        } catch (error) {
          console.warn('Error cleaning up previous React app:', error);
        }
        this.reactRoot = null;
      }

      if (container.hasChildNodes()) {
        container.innerHTML = '';
      }

      if (typeof window !== 'undefined') {
        const { app, api, realtime } = this.configService;

        const authState = await this.auth0Service.getAuthStateForReact();

        (window as any).__AUTH0_BRIDGE__ = {
          getAccessTokenSilently: (options?: any) => this.auth0Service.getAccessTokenSilently(options),
          loginWithRedirect: (redirectTo?: string) => this.auth0Service.loginWithRedirect(redirectTo),
          logout: (returnTo?: string) => this.auth0Service.logout(returnTo),
          isAuthenticated: () => Promise.resolve(this.auth0Service.isAuthenticated()),
          getUser: () => Promise.resolve(this.auth0Service.user()),
        };

        (window as any).__ANGULAR_ENV__ = {
          environment: app.environment,
          version: app.version,
          muiKey: app.muiKey,
          apiHost: api.host,
          baseHost: api.baseHost,
          qrHost: api.qrHost,
          staticHost: api.staticHost,
          defaultTenant: api.tenant.default,
          tenants: api.tenant.available,
          broadcaster: realtime.broadcaster,
          pusherAppID: realtime.pusher.appId,
          pusherKey: realtime.pusher.key,
          pusherSecret: realtime.pusher.secret,
          pusherCluster: realtime.pusher.cluster,
          pusherScheme: realtime.pusher.scheme,
          pusherPort: realtime.pusher.port,
          echoHost: realtime.echo.host,
          reverbAppId: realtime.reverb.appId,
          reverbHost: realtime.reverb.host,
          reverbAppKey: realtime.reverb.appKey,
          auth0State: authState,
          angularShellEnabled: !this.isUseAngular(),
        };
      }

      this.reactRoot = await loadReactApp(container, basePath, {
        isProduction: this.configService.isProduction,
        devServerUrl: this.configService.dev?.reactDevServerUrl,
      });

      this.reactAppBridge.enableReactNotifications();
      await this.reactAppBridge.dispatchInitialAuthState();

      this.subscribeToRouterEvents();

      this.isLoaded.set(true);
    } catch (error) {
      this.hasError.set(true);
      this.isLoaded.set(false);

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('Error details:', errorMessage);
    }
  }

  private subscribeToRouterEvents() {
    this.lastNavigatedPath = this.router.url;

    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const angularPath = event.urlAfterRedirects;

        if (angularPath === this.lastNavigatedPath) {
          return;
        }
        this.lastNavigatedPath = angularPath;

        if (this.isLoaded() && this.reactRoot) {
          if (this.reactRoot.navigate) {
            this.reactRoot.navigate(angularPath, { replace: true });
          } else if ((window as any).__REACT_ROUTER__?.navigate) {
            (window as any).__REACT_ROUTER__.navigate(angularPath, { replace: true });
          }
        }
      });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
      this.routerSubscription = null;
    }

    if (this.reactRoot && typeof this.reactRoot.unmount === 'function') {
      try {
        this.reactRoot.unmount();
      } catch (error) {
        console.warn('Error unmounting React app, attempting manual cleanup:', error);
      } finally {
        this.reactRoot = null;
      }
    }

    if (this.reactContainer?.nativeElement) {
      try {
        const container = this.reactContainer.nativeElement;

        if (container.hasChildNodes()) {
          container.innerHTML = '';
        }
      } catch (error) {
        console.warn('Error clearing container:', error);
      }
    }
  }
}


